import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import * as BeAPI from "../../../../API";
import PageView from "../../../../Components/PageView";
import { ItemProps } from "../Items";
import { BrandProps } from "../Brands";
import { CategoryProps } from "../Categories";

export interface ItemSpecProps {
  id?: string;
  item: string;
  brand?: string;
  unit: string;
  spec?: string;
}

const ItemSpecs = () => {
  const { t } = useTranslation();

  const [data, setData] = useState<ItemSpecProps[]>([]);
  const [items, setItems] = useState<ItemProps[]>([]);
  const [brands, setBrands] = useState<BrandProps[]>([]);

  const getData = () => {
    BeAPI.getAll("categories")
      .then((res: CategoryProps[]) => {
        const sortedCategories = res.sort((a, b) =>
          a.category.toLowerCase() > b.category.toLowerCase() ? 1 : -1
        );

        BeAPI.getAll("items")
          .then((res: ItemProps[]) => {
            const sortedItems = res.sort((a, b) => {
              const categoryA = sortedCategories.find(
                ({ id }) => id === String(a.category)
              );
              const categoryB = sortedCategories.find(
                ({ id }) => id === String(b.category)
              );

              const categoryAName = categoryA?.category.toLowerCase() || "";
              const categoryBName = categoryB?.category.toLowerCase() || "";

              if (categoryAName < categoryBName) return -1;
              if (categoryAName > categoryBName) return 1;

              const itemAName = a.name.toLowerCase();
              const itemBName = b.name.toLowerCase();

              return itemAName < itemBName ? -1 : itemAName > itemBName ? 1 : 0;
            });
            setItems(sortedItems);

            BeAPI.getAll("brands")
              .then((res: BrandProps[]) => {
                const sortedBrands = res.sort((a, b) =>
                  a.brand.toLowerCase() > b.brand.toLowerCase() ? 1 : -1
                );

                setBrands(sortedBrands);

                BeAPI.getAll("item-specs")
                  .then((res: ItemSpecProps[]) =>
                    setData(
                      res.sort((a, b) => {
                        const itemA = sortedItems.find(
                          ({ id }) => id === String(a.item)
                        );
                        const itemB = sortedItems.find(
                          ({ id }) => id === String(b.item)
                        );

                        const categoryA = sortedCategories.find(
                          ({ id }) => id === String(itemA?.category)
                        );
                        const categoryB = sortedCategories.find(
                          ({ id }) => id === String(itemB?.category)
                        );

                        const categoryAName =
                          categoryA?.category.toLowerCase() || "";
                        const categoryBName =
                          categoryB?.category.toLowerCase() || "";

                        if (categoryAName < categoryBName) return -1;
                        if (categoryAName > categoryBName) return 1;

                        const itemAName = itemA?.name.toLowerCase() || "";
                        const itemBName = itemB?.name.toLowerCase() || "";

                        if (itemAName < itemBName) return -1;
                        if (itemAName > itemBName) return 1;

                        const brandA = sortedBrands.find(
                          ({ id }) => id === String(a?.brand)
                        );
                        const brandB = sortedBrands.find(
                          ({ id }) => id === String(b?.brand)
                        );

                        const brandAName = brandA?.brand.toLowerCase() || "";
                        const brandBName = brandB?.brand.toLowerCase() || "";

                        return brandAName < brandBName
                          ? -1
                          : brandAName > brandBName
                          ? 1
                          : 0;
                      })
                    )
                  )
                  .catch((err) => console.log({ err }));
              })
              .catch((err) => console.log({ err }));
          })
          .catch((err) => console.log({ err }));
      })
      .catch((err) => console.log({ err }));
  };

  useEffect(() => {
    getData();
  }, []);

  const formInputs = [
    {
      name: "item",
      label: t("Services.Items.Item"),
      type: "select",
      options: items?.map(({ id, name }) => ({
        value: id || "",
        label: name,
      })),
      render: (row: ItemSpecProps, i: number) => {
        const item = items.find(({ id }) => id === String(row.item));

        return item?.name || "-";
      },
      required: true,
    },
    {
      name: "brand",
      label: t("Services.Brands.Brand"),
      type: "select",
      options: brands?.map(({ id, brand }) => ({
        value: id || "",
        label: brand,
      })),
      render: (row: ItemSpecProps, i: number) => {
        const brand = brands.find(({ id }) => id === String(row.brand));

        return brand?.brand || "-";
      },
      required: false,
    },
    {
      name: "unit",
      label: t("Services.ItemSpecs.Unit"),
      required: true,
    },
    {
      name: "spec",
      label: t("Services.ItemSpecs.Spec"),
      required: false,
    },
  ];

  const onSubmit = (values: ItemSpecProps) => {
    BeAPI.create("item-specs", values).then(() => {
      getData();
    });
  };

  const onDelete = (id: string) =>
    BeAPI.remove("item-specs", id)
      .then(() => {
        getData();
      })
      .catch((err) => console.log({ err }));

  return (
    <PageView
      title={t("Services.ItemSpecs.ItemSpecsList")}
      data={data}
      inputs={formInputs}
      onSubmit={onSubmit}
      onDelete={onDelete}
    />
  );
};

export default ItemSpecs;
